import React, { FC } from 'react';
import { Props } from '@components/v2/VehicleSummary/index';
import TitleWithBenefits from '@components/v2/TitleWithBenefits';
import { getFormattedOfferValidityDate } from '@utils/Car.utils';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import { useOrdelPanelLogic } from '@hooks/useOrdelPanelLogic';
import {
    FINANCE_DETAIL_TOKEN,
    FINANCE_DETAIL_VIEW,
    isBrandDS,
    isBrandOV,
    isMarketGB,
    isMarketIT,
} from 'src/constants/main';
import PriceSuffix from '@components/PriceSuffix';
import IconButton from '@components/IconButton';
import { DepositText } from '@components/DepositText';
import { renderPromotionalText } from '@utils/Price.utils';
import AccessibleButton from '@components/Button/AccessibleButton';
import { getAgentDiscount, getPromoCodeDiscount } from '@utils/Deal.utils';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useJourneyType } from '@hooks/useJourneyType';
import parser from 'bbcode-to-react';
import { useMediaQuery } from '@hooks/useMediaquery';
import { useTheme } from '@hooks/useTheme';

export const VehicleSummaryTemplate: FC<Props> = ({ className }) => {
    const {
        t,
        formatMoney,
        carDetails,
        isCataloguePriceFeatureSwitchEnabled,
        isCustomDepositTextAllowed,
        processCalculateSummaryDataPromise,
        catalog,
        totalBasicPrice,
        totalCashPrice,
        totalMonthlyPrice,
        deposit,
        apr,
        isCataloguePriceEqualToStorePrice,
        hideCashPayment,
        infoIconType,
        amountRounding,
        financeDetailView,
        financeDetailText,
        showFOLink,
        promoText,
    } = useOrdelPanelLogic();
    const theme = useTheme();
    const { currentDeal } = useCarDetailsDuck();
    const { paymentJourneyType } = useJourneyType();

    const offerValidityDate = getFormattedOfferValidityDate(carDetails?.importInputs);
    const showOfferValidityDate: boolean = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_OFFER_VALIDITY_DATE_ENABLED
    );
    const offerValidityDateString =
        showOfferValidityDate && offerValidityDate ? `${t('basket.offerValidityDate')} ${offerValidityDate}` : '';

    const { hasSelectedPaymentJourney = false } = currentDeal?.extraFields ?? {};

    const totalCashPriceWithoutDiscount =
        totalCashPrice +
        getPromoCodeDiscount(currentDeal, hasSelectedPaymentJourney ? paymentJourneyType : null) +
        getAgentDiscount(currentDeal);

    const sm = useMediaQuery(`(max-width: ${theme.flexboxgrid.breakpoints.sm}em)`);

    return (
        <div className={className}>
            {!sm && <TitleWithBenefits withBenefits={false} onlyTitle />}
            <div className="summary-price-container">
                <div className="content">
                    <span className="header">
                        {isCataloguePriceFeatureSwitchEnabled &&
                            (!isCataloguePriceEqualToStorePrice || (hideCashPayment && !isMarketGB)) && (
                                <>
                                    <span className="catalog-price-title">
                                        <span className="catalogPrice">{t('price.catalog.title')}&nbsp;</span>
                                        {formatMoney(
                                            catalog?.totalPrice !== undefined
                                                ? catalog.totalPrice
                                                : totalCashPriceWithoutDiscount,
                                            true
                                        )}
                                    </span>
                                    <span className="divider">|</span>
                                </>
                            )}
                    </span>
                    <div className="body">
                        <div className="body-wrapper">
                            <div className="heading-wrapper">
                                <span className="heading">
                                    <span className="heading-content">
                                        {!isCataloguePriceEqualToStorePrice ? (
                                            <>
                                                <span>{t('offerListItem.specialPriceText')}</span>
                                                {isBrandDS && !isMarketGB && (
                                                    <span className="priceFromText">{t('nameplate.priceFrom')}</span>
                                                )}
                                            </>
                                        ) : (
                                            t(`offerListItem.standardPriceText${carDetails?.VDStatus ? '.demoVehicle' : ''}`)
                                        )}
                                    </span>
                                </span>
                                {!hideCashPayment && (
                                    <span className="price" data-testid="TESTING_VEHICLE_SUMMARY_STORE_PRICE">
                                        {formatMoney(
                                            carDetails?.stock ? totalBasicPrice : totalCashPriceWithoutDiscount,
                                            true
                                        )}
                                        <PriceSuffix showOnlyPrioritizedTranslation checkPrioritizedTranslation />
                                    </span>
                                )}
                            </div>
                            {totalMonthlyPrice && (
                                <>
                                    <div className="totalMonthlyPriceContainer">
                                        <div
                                            className="totalMonthlyPriceWrapper"
                                            data-testid="TESTING_VEHICLE_SUMMARY_MONTHLY_PRICE"
                                        >
                                            <span className="price">
                                                <span className="or">{t('configurator.optionPrice.suffix')}</span>
                                                {formatMoney(totalMonthlyPrice, false, '', amountRounding)}
                                            </span>
                                            <span className="prefix">
                                                <PriceSuffix checkPrioritizedTranslation isMonthlyPrice />
                                            </span>
                                            {financeDetailView === FINANCE_DETAIL_VIEW.ICON && (
                                                <IconButton
                                                    className="infoIcon"
                                                    icon={infoIconType}
                                                    width={16}
                                                    height={16}
                                                    onClick={processCalculateSummaryDataPromise}
                                                />
                                            )}
                                        </div>
                                        <span
                                            className={`footer ${
                                                financeDetailView === FINANCE_DETAIL_VIEW.ICON &&
                                                'footer-icon-displayed'
                                            }`}
                                        >
                                            {isCustomDepositTextAllowed ? (
                                                <DepositText />
                                            ) : (
                                                <>
                                                    {isMarketGB &&
                                                        (promoText?.length > 0 || promoText?.primaryText?.length > 0) &&
                                                        showFOLink && (
                                                            <div className="apr">
                                                                <p className="aprValue">{`${apr} % ${t(
                                                                    'landingPage.solCard.aprSuffix'
                                                                )}`}</p>
                                                            </div>
                                                        )}
                                                    {!isMarketGB &&
                                                        renderPromotionalText(
                                                            promoText,
                                                            isMarketIT && isBrandOV
                                                                ? t('price.pattern.anticipo')
                                                                : t('price.pattern'),
                                                            deposit,
                                                            null,
                                                            null,
                                                            null,
                                                            null,
                                                            { apr }
                                                        )}
                                                </>
                                            )}
                                            {financeDetailView === FINANCE_DETAIL_VIEW.LINK && (
                                                <div>
                                                    <AccessibleButton
                                                        onClick={processCalculateSummaryDataPromise}
                                                        className="financeContainer-price-financeQuoteLink"
                                                        data-testid="TESTING_PANEL_INFOICON"
                                                    >
                                                        {isMarketGB || financeDetailText === FINANCE_DETAIL_TOKEN
                                                            ? t('landingPage.solCard.bonus.link')
                                                            : parser.toReact(financeDetailText)}
                                                    </AccessibleButton>
                                                </div>
                                            )}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {Boolean(offerValidityDateString) && (
                <span className="offerValidityDate">{`${t('basket.offerValidityDate')} ${offerValidityDate}`}</span>
            )}
            <div className="benefits-wrapper">
                <TitleWithBenefits onlyBenefits />
            </div>
        </div>
    );
};
